import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  doc, 
  updateDoc,
  getDocs,
  writeBatch
} from 'firebase/firestore';
import { db } from '../../firebase/firebase';

/**
 * Subscribe to seller notifications
 * @param {string} sellerId - The seller's UID
 * @param {function} onUpdate - Callback function receiving the list of notifications
 * @returns {function} Unsubscribe function
 */
export const subscribeToNotifications = (sellerId, onUpdate) => {
  if (!sellerId) return () => {};

  // Simple query to avoid composite index requirement
  // We will sort and limit on the client side
  const q = query(
    collection(db, 'notifications'),
    where('sellerId', '==', sellerId)
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const notifications = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      // Handle Timestamp objects safely
      createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : new Date(doc.data().createdAt || Date.now())
    }));
    
    // Client-side sort: Newest first
    notifications.sort((a, b) => b.createdAt - a.createdAt);
    
    // Client-side limit: Top 50
    const recentNotifications = notifications.slice(0, 50);
    
    onUpdate(recentNotifications);
  }, (error) => {
    console.error("Error listening to notifications:", error);
    // Call onUpdate with empty array to clear loading state in UI
    onUpdate([]); 
  });

  return unsubscribe;
};

/**
 * Mark a single notification as read
 * @param {string} notificationId 
 */
export const markNotificationAsRead = async (notificationId) => {
  try {
    const docRef = doc(db, 'notifications', notificationId);
    await updateDoc(docRef, { isRead: true });
    return true;
  } catch (error) {
    console.error("Error marking notification as read:", error);
    return false;
  }
};

/**
 * Mark all notifications as read for a seller
 * @param {string} sellerId 
 */
export const markAllNotificationsAsRead = async (sellerId) => {
    try {
        const q = query(
            collection(db, 'notifications'),
            where('sellerId', '==', sellerId),
            where('isRead', '==', false)
        );
        const snapshot = await getDocs(q);
        
        if (snapshot.empty) return true;

        const batch = writeBatch(db);
        snapshot.docs.forEach((doc) => {
            batch.update(doc.ref, { isRead: true });
        });

        await batch.commit();
        return true;
    } catch (error) {
        console.error("Error marking all as read:", error);
        return false;
    }
};
